<template>
    <md-dialog :md-active.sync="isDialogVisible" :md-click-outside-to-close="false">
        <md-dialog-title>Bulk Add Users</md-dialog-title>
        <md-dialog-content>
        <md-field v-if="isSuperAdmin">
            <label>Organisation</label>
            <md-select v-model="orgId" name="orgId">
            <md-option
                v-for="organisation in organisations"
                :key="organisation.id"
                :value="organisation.id"
                >{{ organisation.name }}</md-option
            >
            </md-select>
        </md-field>

        <md-field>
            <label>Select file with the list of users</label>
            <md-file v-model="file" accept=".xlsx, .csv" @change="fileSelected" />
        </md-field>

        <md-table v-if="usersToAdd">
            <md-table-row>
            <md-table-head>Email</md-table-head>
            <md-table-head>Password</md-table-head>
            <md-table-head>Name</md-table-head>
            <md-table-head>Status</md-table-head>
            </md-table-row>

            <md-table-row v-for="user in usersToAdd" :key="user.email" :class="getUserRowClass(user)">
            <md-table-cell>{{ user.email }}</md-table-cell>
            <md-table-cell>{{ user.password }}</md-table-cell>
            <md-table-cell>{{ user.displayName }}</md-table-cell>
            <md-table-cell>{{ user.status }}</md-table-cell>
            </md-table-row>
        </md-table>
        </md-dialog-content>

        <md-dialog-actions>
        <md-button
            class="md-accent"
            @click="addUsers"
            v-if="usersToAdd && usersToAdd.length"
            :disabled="started"
        >Add Users</md-button>
        <md-button class="md-primary" @click="closeDialog">Close</md-button>
        </md-dialog-actions>

        <md-snackbar
        :md-position="'center'"
        :md-duration="10000"
        :md-active.sync="showSnackbar"
        md-persistent
        >
        <span>{{ message }}</span>
        </md-snackbar>
    </md-dialog>
</template>

<script>
/*
 * ---------------------------------------------------------------------------
 * COMMERCIAL IN CONFIDENCE
 * 
 * (c) Copyright Quosient Ltd. All Rights Reserved.
 * 
 * See LICENSE.txt in the repository root.
 * ---------------------------------------------------------------------------
*/
import { organisationsCollection } from "../firebase.js";
import { AuthService } from "../services/auth.service";
import { SpreadsheetService } from "@/services/spreadsheet.service";
import { CsvService } from "@/services/csv.service";
import { functions } from "../firebaseFunctions.js";

export default {
    name: "UserListBulkAddUsers",
    data: () => ({
        organisations: null,
        authenticatedUser: null,
        orgId: null,
        file: null,
        usersToAdd: null,
        showSnackbar: false,
        message: "",
        started: false,
        isDialogVisible: false,
    }),
    methods: {
        showDialog() {
            this.file = null;
            this.usersToAdd = null;
            this.message = "";
            this.showSnackbar = false;
            this.started = false;
            this.isDialogVisible = true;
            this.orgId = this.authenticatedUser.orgId;
        },
        closeDialog() {
            this.$emit("dialog-closed");
            this.isDialogVisible = false;
        },
        fileSelected(event) {
            let file = event.target.files[0];

            const extension = file.name.split('.').pop();

            let usersPromise;
            if (extension === 'csv') {
                usersPromise = CsvService.readUsersFromCsv(file);
            } else if (extension === 'xlsx') {
                usersPromise = SpreadsheetService.readUsersFromSpreadsheet(file);
            } else {
                this.message = `File type '${extension}' is not supported.`;
                this.showSnackbar = true;
                return;
            }

            usersPromise.then(
                (usersToAdd) => {
                this.usersToAdd = usersToAdd;
                },
                (error) => {
                console.error(error);
                this.message = `Error while loading users from file '${file.name}'.`;
                this.showSnackbar = true;
                }
            );
        },
        addUsers() {
            if (this.started || !this.usersToAdd || !this.usersToAdd.length) {
                return;
            }

            this.started = true;

            for (let i = 0; i < this.usersToAdd.length; i++) {
                let user = this.usersToAdd[i];
                user.status = 'In Progress';
                user.orgId = this.orgId;
                user.sendEmail = true;

                if (!user.password) {
                user.password = Math.random().toString(36).substr(2, 10);
                }

                functions.addUser(user).then(
                () => {
                    user.status = 'Success';
                    this.$forceUpdate();
                },
                (error) => {
                    console.error(error, user);
                    user.status = `Error: ${error.message}`;
                    this.$forceUpdate();
                }
                );
            }
        },
        getUserRowClass: (user) => ({
            'md-selected-single md-primary warning': user.status != null && user.status.startsWith('Error'),
            'md-selected-single md-accent': user.status === 'Success',
            'md-selected-single': user.status === 'In Progress'
        })
    },
    computed: {
        isSuperAdmin() {
        return this.authenticatedUser.roles && this.authenticatedUser.roles.includes("superadmin");
        }
    },
    created: function () {
        this.subscription = AuthService.loggedUser$.subscribe((user) => {
        this.authenticatedUser = user;
        this.orgId = user.orgId;

        let isSuperAdmin = user.roles && user.roles.includes("superadmin");

        if (isSuperAdmin && !this.organisations) {
            organisationsCollection.get().then((querySnapshot) => {
                let organisations = [];
                querySnapshot.forEach((doc) => {
                    let docData = doc.data();
                    organisations.push({
                    id: doc.id,
                    name: docData.name,
                    });
                });
                this.organisations = organisations;
            });
        }
        });
    },
    beforeDestroy() {
        this.subscription.unsubscribe();
    },
};
</script>

<style scoped lang="scss">
    .md-table.md-theme-default .md-selected-single.md-primary.warning {
        background-color: #ef4748;
    }

    @media (min-width: 601px) {
        .md-dialog-content {
            width: 80vw;
        }
    }
</style>